import { useState, useEffect } from "react";
import { Plus, Trash2, ArrowUp, ArrowDown, GraduationCap, Briefcase } from "lucide-react";
import { useSettingsSection } from "@/lib/useSettingsSection";
import { PageTitle, Card, Field, inputCls, SaveButton } from "@/components/admin/ui";
import { ImageField } from "@/components/admin/MediaPicker";

function Repeater({ items, setItems, fields, addLabel, testid }) {
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= items.length) return;
    const n = [...items];
    [n[i], n[j]] = [n[j], n[i]];
    setItems(n);
  };
  const update = (i, key, val) => { const n = [...items]; n[i] = { ...n[i], [key]: val }; setItems(n); };
  return (
    <div data-testid={testid}>
      {items.map((it, i) => (
        <div key={i} className="border border-slate-200 rounded-sm p-4 mb-3 bg-slate-50/50" data-testid={`${testid}-item-${i}`}>
          <div className="flex justify-end gap-1 mb-2">
            <button type="button" onClick={() => move(i, -1)} disabled={i === 0} className="p-1.5 text-slate-400 hover:text-navy disabled:opacity-30"><ArrowUp size={14} /></button>
            <button type="button" onClick={() => move(i, 1)} disabled={i === items.length - 1} className="p-1.5 text-slate-400 hover:text-navy disabled:opacity-30"><ArrowDown size={14} /></button>
            <button type="button" onClick={() => setItems(items.filter((_, x) => x !== i))} className="p-1.5 text-slate-400 hover:text-red-500" data-testid={`${testid}-delete-${i}`}><Trash2 size={14} /></button>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            {fields.map((f) => (
              <div key={f.key} className={f.full ? "sm:col-span-2" : ""}>
                <label className="block text-[11px] font-semibold text-slate-500 mb-1 uppercase tracking-wide">{f.label}{f.optional ? " (optional)" : ""}</label>
                {f.textarea
                  ? <textarea rows={2} className={inputCls} value={it[f.key] || ""} onChange={(e) => update(i, f.key, e.target.value)} placeholder={f.placeholder} />
                  : <input className={inputCls} value={it[f.key] || ""} onChange={(e) => update(i, f.key, e.target.value)} placeholder={f.placeholder} />}
              </div>
            ))}
          </div>
        </div>
      ))}
      <button type="button" onClick={() => setItems([...items, {}])} className="text-brass text-sm inline-flex items-center gap-1 mb-2" data-testid={`${testid}-add`}><Plus size={15} /> {addLabel}</button>
    </div>
  );
}

export default function AdminProfile() {
  const { settings, saving, save } = useSettingsSection();
  const [p, setP] = useState({});
  useEffect(() => { if (settings) setP({ qualifications: [], experiences: [], ...settings.profile }); }, [settings]);
  if (!settings) return <div className="text-slate-400">Loading…</div>;
  const set = (k) => (e) => setP({ ...p, [k]: e.target.value });

  return (
    <div data-testid="admin-profile">
      <PageTitle title="Profile" subtitle="Edit the About / Profile page content" />
      <div className="space-y-6">
        <Card title="Profile Photo">
          <p className="text-slate-500 text-sm mb-4">Displayed as a transparent cut-out on the Profile hero and homepage. Replace anytime — the public site updates immediately.</p>
          <ImageField value={p.portrait_url} onChange={(v) => setP({ ...p, portrait_url: v })} testid="profile-photo-field" />
        </Card>

        <Card title="Professional Introduction">
          <Field label="Introduction"><textarea rows={5} className={inputCls} value={p.intro || ""} onChange={set("intro")} data-testid="profile-intro-input" /></Field>
          <SaveButton onClick={() => save({ profile: p })} saving={saving} testid="save-profile-intro" label="Save Introduction" />
        </Card>

        <Card title={<span className="flex items-center gap-2"><GraduationCap size={18} className="text-brass" /> Education & Qualifications</span>}>
          <Repeater
            items={p.qualifications || []}
            setItems={(v) => setP({ ...p, qualifications: v })}
            addLabel="Add Qualification"
            testid="qualifications"
            fields={[
              { key: "qualification", label: "Qualification", placeholder: "e.g. LL.B." },
              { key: "field", label: "Specialization / Subject", placeholder: "e.g. Bachelor of Laws" },
              { key: "institution", label: "Institution", optional: true },
              { key: "year", label: "Year", optional: true },
              { key: "description", label: "Description", optional: true, full: true, textarea: true },
            ]}
          />
          <div className="mt-3"><SaveButton onClick={() => save({ profile: p })} saving={saving} testid="save-qualifications" label="Save Qualifications" /></div>
        </Card>

        <Card title={<span className="flex items-center gap-2"><Briefcase size={18} className="text-brass" /> Professional Experience</span>}>
          <Repeater
            items={p.experiences || []}
            setItems={(v) => setP({ ...p, experiences: v })}
            addLabel="Add Experience"
            testid="experiences"
            fields={[
              { key: "role", label: "Position / Role", placeholder: "e.g. Counsel" },
              { key: "organization", label: "Organization", optional: true },
              { key: "start_year", label: "Start Year", optional: true, placeholder: "e.g. 2017" },
              { key: "end_year", label: "End Year", optional: true, placeholder: "e.g. Present" },
              { key: "description", label: "Description", full: true, textarea: true },
            ]}
          />
          <div className="mt-3"><SaveButton onClick={() => save({ profile: p })} saving={saving} testid="save-experiences" label="Save Experience" /></div>
        </Card>

        <Card title="Other Profile Content">
          <Field label="Professional Membership" hint="Optional — leave blank or use a placeholder."><textarea rows={2} className={inputCls} value={p.membership || ""} onChange={set("membership")} /></Field>
          <Field label="Professional Philosophy"><textarea rows={3} className={inputCls} value={p.philosophy || ""} onChange={set("philosophy")} /></Field>
          <Field label="Thought on Law & Society"><textarea rows={3} className={inputCls} value={p.thought || ""} onChange={set("thought")} data-testid="profile-thought-input" /></Field>
          <Field label="Practice Location"><textarea rows={2} className={inputCls} value={p.practice_location || ""} onChange={set("practice_location")} /></Field>
          <SaveButton onClick={() => save({ profile: p })} saving={saving} testid="save-profile" />
        </Card>
      </div>
    </div>
  );
}
